import { getApiUrl } from '@/lib/api-config';

/**
 * Time Tracking's data layer: every read and write goes through the backend
 * API (nothing here talks to Supabase). Durations are whole SECONDS computed
 * by the server from start/end; the company, the user and `hours` are never
 * sent -- the server owns them.
 */

export interface TimeEntryRow {
  id: string;
  project_id: string;
  project_name: string | null;
  user_id: string;
  user_name: string | null;
  date: string;
  start_time: string | null;
  end_time: string | null;
  description: string;
  duration_seconds: number;
}

export interface TimeStats {
  total_seconds: number;
  entry_count: number;
  average_seconds: number;
  most_active_project: { name: string; seconds: number } | null;
  active_projects: number;
  project_breakdown: Array<{ id: string; name: string; seconds: number; percentage: number }>;
  employee_breakdown: Array<{ id: string; name: string; seconds: number; percentage: number }>;
}

async function readJson(response: Response, fallback: string) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || body?.message || fallback);
  return body;
}

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

export async function fetchTimeStats(range: { from: string; to: string }): Promise<TimeStats> {
  const params = new URLSearchParams(range);
  const body = await readJson(await fetch(getApiUrl(`api/time-entries/stats?${params}`)), 'Failed to fetch statistics');
  return body.data;
}

export interface TimeEntryInput {
  projectId: string;
  /** Local calendar day of the start (YYYY-MM-DD). */
  date: string;
  startISO: string;
  endISO: string;
}

export async function createTimeEntry(input: TimeEntryInput): Promise<{ id: string }> {
  const body = await readJson(
    await fetch(getApiUrl('api/entities/time_entry'), json('POST', {
      project_id: input.projectId, date: input.date, start_time: input.startISO, end_time: input.endISO, billable: true,
    })),
    'Failed to create time entry'
  );
  return body.data;
}

/** Only the fields the form edits are sent: an existing description / billable flag is never overwritten. */
export async function updateTimeEntry(id: string, input: TimeEntryInput): Promise<void> {
  await readJson(
    await fetch(getApiUrl(`api/entities/time_entry/${id}`), json('PUT', {
      project_id: input.projectId, date: input.date, start_time: input.startISO, end_time: input.endISO,
    })),
    'Failed to update time entry'
  );
}

export async function deleteTimeEntry(id: string): Promise<void> {
  await readJson(await fetch(getApiUrl(`api/entities/time_entry/${id}`), json('DELETE')), 'Failed to delete time entry');
}
